import { AbstractControl, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { UserModel } from '@tsin-core/models/auth.model';
import { UserRoleEnum } from '@tsin-shared/enums/UserRoleEnum';
import { environment } from '@tsin-env/environment';

// Form Utilities for Validation
export function getFormField(form: FormGroup, fieldName: string): AbstractControl {
  if (form) {
    const field: AbstractControl | null = form.get(fieldName);
    return field ? field : new FormControl('');
  }
  return new FormControl('');
}

export function isOlderThan12Hours(dateString: string | undefined): boolean {
  if (!dateString) {
    return false;
  }
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const hoursDiff = diffInMs / (1000 * 60 * 60);
  return hoursDiff > 12; // Updated to 12 hours
}

export function formatDateCustom(dateString: string): string {
  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, '0'); // Day with leading zero
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Month with leading zero (0-based index)
  const year = date.getFullYear(); // Full year

  return `${day}-${month}-${year}`;
}

export function getDateFmt(dateStr: Date): string {
  const date = new Date(dateStr);

  // Create a formatted string with custom logic for day suffix (st, nd, rd, th)
  const day = date.getDate();
  const daySuffix = (day: any) => {
    if (day > 3 && day < 21) return 'th'; // for 4th to 20th
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  // Format options for day name and month
  const options = { weekday: 'long', month: 'long', year: 'numeric' };
  return `${date.toLocaleDateString('en-US', { weekday: 'long' })} ${day}${daySuffix(day)} of ${date.toLocaleDateString('en-US', { month: 'long' })}, ${date.getFullYear()}`;
}

export function formatTime(dateInput: Date): string {
  const date = new Date(dateInput);
  const timeZone: string = 'America/New_York'; // Assuming EDD corresponds to Eastern Daylight Time
  return formatDateToTimeWithTimeZone(date, timeZone);
}

export function formatDateToTimeWithTimeZone(date: Date, timeZone: string): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
    timeZone,
  };

  return new Intl.DateTimeFormat('en-US', options).format(date);
}

export function passwordMatchValidator(form: FormGroup) {
  const password = form.get('password')?.value;
  const confirmPassword = form.get('confirmPassword')?.value;
  // Check if both passwords match
  return password === confirmPassword ? null : { mismatch: true };
}

export function validateFormField(fieldName: string, form: FormGroup): AbstractControl {
  if (form) {
    const field = form.get(fieldName);
    return field ? field : new FormControl('');
  }
  return new FormControl('');
}

/**
 * Roles that belong in the /management tree. adminGuard gates that tree with the
 * same list, so the two must stay in sync: a role missing here is sent to /user
 * at login even though the guard would have let it into /management.
 *
 * Roles appartenant a l'arborescence /management. adminGuard protege cette
 * arborescence avec la meme liste : les deux doivent rester synchronisees.
 */
export const MANAGEMENT_ROLES: readonly string[] = Object.freeze([
  UserRoleEnum.SUPER_ADMIN,
  UserRoleEnum.ADMIN,
  UserRoleEnum.PRO_ADMIN,
  UserRoleEnum.PRO_ADMIN_QC,
  UserRoleEnum.PRO_MANAGER,
  // PRO Screener Queue & QC Workflow / File d'attente des examinateurs et flux d'AQ
  UserRoleEnum.PRO_SCREENER,
  UserRoleEnum.PRO_QC_REVIEWER,
  UserRoleEnum.PRO_CD_REVIEWER,
]);

export function determineAuthStatus(user: UserModel | null): string {
  const role: string = user?.role ?? '';

  if (MANAGEMENT_ROLES.includes(role)) {
    return `/management`;
  }

  switch (role) {
    case 'schoolAdmin':
      return `/university`;

    case 'proModerator':
    case 'proInterviewer':
      return `/staff`;

    case 'learnerStudent':
    case 'student':
    default:
      return `/user`;
  }
}

/**
 * Returns true for admin/staff roles that are allowed to switch language freely.
 * Candidates (student/learnerStudent) are locked to their registration language.
 *
 * Renvoie vrai pour les rôles admin/personnel autorisés à changer de langue librement.
 * Les candidats sont verrouillés sur la langue de leur inscription.
 */
export function isStaffOrAdminRole(role: string | null | undefined): boolean {
  const staffRoles = [...MANAGEMENT_ROLES, 'schoolAdmin', 'proModerator', 'proInterviewer', 'facilitatorAdmin'];
  return !!role && staffRoles.includes(role);
}

export function encrypt(password: string) {
  return CryptoJS.AES.encrypt(password, environment.salt).toString();
}

export function lettersOnlyValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  const lettersOnly = /^[A-Za-z]+$/;

  if (value && !lettersOnly.test(value)) {
    return { lettersOnly: true };
  }
  return null;
}

export function numbersOnlyValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  // Regex allows integers (e.g. "123") and decimals (e.g. "123.45")
  const numbersAndDecimalRegex = /^\d+(\.\d+)?$/;

  if (value && !numbersAndDecimalRegex.test(value)) {
    return { numbersOnly: true };
  }
  return null;
}

export function numericOnlyValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  const numbersOnly = /^[0-9]+$/;

  if (value && !numbersOnly.test(value)) {
    return { numbersOnly: true };
  }
  return null;
}

export function phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) {
    return null;
  }

  // Check if phone number starts with + (country code indicator)
  const hasCountryCode = /^\+[0-9]+/.test(value);

  if (!hasCountryCode) {
    return { missingCountryCode: true };
  }

  // Check if the rest of the phone number contains only digits
  const phoneRegex = /^\+[0-9]+$/;
  if (!phoneRegex.test(value)) {
    return { invalidPhoneFormat: true };
  }

  // Check minimum length (country code + at least 7 digits)
  if (value.length < 8) {
    // +1 + 7 digits minimum
    return { phoneTooShort: true };
  }

  return null;
}

export function phoneNumberKeyFilter(event: KeyboardEvent): boolean {
  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

  // Allow navigation and control keys
  if (allowedKeys.includes(event.key)) {
    return true;
  }

  // Allow + symbol only at the beginning
  if (event.key === '+') {
    const target = event.target as HTMLInputElement;
    return target.selectionStart === 0 && !target.value.includes('+');
  }

  // Allow only digits
  return /^[0-9]$/.test(event.key);
}
